import React, { useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { formatters } from "components/DashboardNew/config";
import { formatCompactNumber } from "components/DashboardNew/widgets/Meta/metaUtils";
import { getSelectionIndex } from "utils/dashboardChart";

export default function BarChart({
  data = [],
  theme = {},
  orientation = "vertical",
  dataContext = {},
  onExposeLegend,
  onDataPointClick,
}) {
  let names = Array.isArray(data) ? data.map((d) => d?.name) : [];
  let values = Array.isArray(data) ? data.map((d) => d?.value) : [];

  if (
    typeof theme?.xAxisKeyFormatter === "string" &&
    formatters[theme?.xAxisKeyFormatter]
  ) {
    names = names.map((name) => formatters[theme?.xAxisKeyFormatter](name));
  }
  if (
    typeof theme?.yAxisKeyFormatter === "string" &&
    formatters[theme?.yAxisKeyFormatter]
  ) {
    values = values.map((value) => formatters[theme?.yAxisKeyFormatter](value));
  }

  const [activeIndex, setActiveIndex] = useState(() =>
    getSelectionIndex({ names, values, theme })
  );

  const baseColor = theme.barColor || "rgba(242, 128, 0, 0.15)";
  const hiColor = theme.highlightColor || "#F28000";
  const isHorizontal =
    orientation === "horizontal" || theme.orientation === "horizontal";

  const colors = useMemo(
    () => values.map((_, i) => (i === activeIndex ? hiColor : baseColor)),
    [values, activeIndex, hiColor, baseColor]
  );
  const series = useMemo(() => {
    if (isHorizontal) {
      const pointData = names.map((n, i) => ({ x: n, y: values[i] }));
      return [{ data: pointData }];
    }
    return [{ data: values }];
  }, [isHorizontal, names, values]);

  const options = useMemo(
    () => ({
      chart: {
        parentHeightOffset: 0,
        toolbar: {
          show: false,
        },
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            const idx = config?.dataPointIndex;
            if (typeof idx === "number") {
              setActiveIndex(idx);
              try {
                setWasClicked(true);
              } catch (_e) {}
              if (typeof onDataPointClick === "function") {
                const name = names?.[idx];
                const value = values?.[idx];
                const xKey = theme?.xAxisKey || "x";
                const yKey = theme?.yAxisKey || "y";
                const payload = {
                  index: idx,
                  label: name,
                  value,
                  name,
                  xKey,
                  yKey,
                  [xKey]: name,
                  [yKey]: value,
                  type: "bar",
                  orientation: isHorizontal ? "horizontal" : "vertical",
                  row: Array.isArray(dataContext?.rows)
                    ? dataContext?.rows[idx]
                    : undefined,
                };
                try {
                  onDataPointClick(payload);
                } catch (_e) {}
              }
            }
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: isHorizontal,
          borderRadius: 4,  
          distributed: true,
          //columnWidth: isHorizontal ? "55%" : "35%",
          columnWidth: theme?.columnWidth ?? (isHorizontal ? "55%" : "35%"),
          startingShape: "rounded",
          dataLabels: { position: isHorizontal ? "right" : "top" },
        },
      },
      // theme.legend: boolean or { show, position, horizontalAlign }
      legend: {
        show:
          theme?.legend === false
            ? false
            : theme?.legend === true
            ? true
            : typeof theme?.legend?.show === "boolean"
            ? theme.legend.show
            : false,
        position:
          typeof theme?.legend?.position === "string"
            ? theme.legend.position
            : "top",
        horizontalAlign:
          typeof theme?.legend?.horizontalAlign === "string"
            ? theme.legend.horizontalAlign
            : "left",
      },
      tooltip: { enabled: true },
      dataLabels: {
        enabled: theme?.showBarValues !== false,
        offsetY: isHorizontal ? 0 : -20,
        offsetX: isHorizontal ? 20 : 0,
        // formatter: val => val != 0 ? `${formatCompactNumber(val)}` : "",
        formatter: (val) => {
          if (typeof theme?.barValueFormatter === "function") {
            return theme.barValueFormatter(val);
          }

          return val != 0 ? `${formatCompactNumber(val)}` : "";
        },
        style: { fontWeight: 500, colors: ["#94a3b8"], fontSize: "12px" },
      },
      colors,
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
      grid: {
        show: Boolean(theme?.showGridX || theme?.showGridY),
        xaxis: { lines: { show: Boolean(theme?.showGridX) } },
        yaxis: { lines: { show: Boolean(theme?.showGridY) } },
        padding: isHorizontal
          ? {
              top: theme?.showBarValues !== false ? 16 : 12,
              left: 32,
              right: 8,
              bottom: 0,
            }
          : {
              top: theme?.showBarValues !== false ? 28 : 20,
              left: -5,
              right: -8,
              bottom: -12,
            },
      },
      xaxis: isHorizontal
        ? {
            // numeric axis when horizontal
            title: {
              text: theme.xAxisTitle || undefined,
              style: { color: "#9ca3af", fontSize: "12px", fontWeight: 500 },
            },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showXAxisLine) },
            labels: {
              show: theme?.showXAxisLabels !== false,
              formatter: (val) => `${formatCompactNumber(Number(val))}`,
              style: { colors: "#9ca3af", fontSize: "12px" },
            },
          }
        : {
            // categorical axis when vertical
            categories: names,
            title: {
              text: theme.xAxisTitle || undefined,
              style: { color: "#9ca3af", fontSize: "12px", fontWeight: 500 },
            },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showXAxisLine) },
            labels: {
              show: theme?.showXAxisLabels !== false,
              rotate: -35,
              trim: false,
              hideOverlappingLabels: false,
              //   style: { colors: "#9ca3af", fontSize: "12px" },
              style: {
                colors: [theme?.xAxisLabelColor ?? "#9ca3af"],
                fontSize: theme?.xAxisLabelFontSize ?? "12px",
                fontWeight: theme?.xAxisLabelFontWeight ?? 400,
              },
            },
          },
      yaxis: isHorizontal
        ? {
            // categorical axis when horizontal; categories also inferred from point {x}
            categories: names,
            title: {
              text: theme.yAxisTitle || undefined,
              style: { color: "#9ca3af", fontSize: "12px", fontWeight: 500 },
            },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showYAxisLine) },
            labels: {
              show: theme?.showYAxisLabels !== false,
              offsetX: 0,
              maxWidth: 220,
              style: { colors: "#9ca3af", fontSize: "12px" },
            },
          }
        : {
            // numeric axis when vertical
            title: {
              text: theme.yAxisTitle || undefined,
              style: { color: "#9ca3af", fontSize: "12px", fontWeight: 500 },
            },
            axisTicks: { show: false },
            axisBorder: { show: Boolean(theme?.showYAxisLine) },
            labels: {
              show: theme?.showYAxisLabels !== false,
              offsetX: -15,
              formatter: (val) => `${formatCompactNumber(val)}`,
              style: { colors: "#9ca3af", fontSize: "12px" },
            },
          },
      labels: {
        show: true,
      },
    }),
    [names, colors, isHorizontal, theme, orientation]
  );

  // Keep default selection in sync with data/theme changes unless user clicked
  const [wasClicked, setWasClicked] = useState(false);
  const lastEmittedIndexRef = React.useRef(-1);
  const emitSelection = (idx) => {
    if (typeof onDataPointClick !== "function") return;
    if (typeof idx !== "number" || idx < 0) return;
    const name = names?.[idx];
    const value = values?.[idx];
    const xKey = theme?.xAxisKey || "x";
    const yKey = theme?.yAxisKey || "y";
    const payload = {
      index: idx,
      label: name,
      value,
      name,
      xKey,
      yKey,
      [xKey]: name,
      [yKey]: value,
      type: "bar",
      orientation: isHorizontal ? "horizontal" : "vertical",
      row: Array.isArray(dataContext?.rows)
        ? dataContext?.rows[idx]
        : undefined,
    };
    try {
      onDataPointClick(payload);
    } catch (_e) {}
  };
  React.useEffect(() => {
    if (!wasClicked) {
      const idx = getSelectionIndex({ names, values, theme });
      if (typeof idx === "number" && idx !== activeIndex) setActiveIndex(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(names),
    JSON.stringify(values),
    JSON.stringify(theme?.selection),
    theme?.highlightIndex,
    theme?.highlightX,
  ]);

  // Emit initial/default selection so consumers receive selectedData immediately
  React.useEffect(() => {
    if (
      names?.length &&
      values?.length &&
      typeof activeIndex === "number" &&
      activeIndex >= 0 &&
      lastEmittedIndexRef.current !== activeIndex
    ) {
      emitSelection(activeIndex);
      lastEmittedIndexRef.current = activeIndex;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, JSON.stringify(names), JSON.stringify(values)]);

  // Expose legend-like color mapping for bars after render commit
  const exposedItems = useMemo(
    () => names.map((n, i) => ({ label: n, color: colors[i] })),
    [names, colors]
  );
  React.useEffect(() => {
    if (typeof onExposeLegend === "function") {
      onExposeLegend(exposedItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(exposedItems)]);

  return (
    <Chart
      type="bar"
      height={"100%"}
      width={"100%"}
      series={series}
      options={options}
    />
  );
}
